import classNames from "classnames"
import { useEffect, type FC, useState } from "react"
import Button from "~/components/Button"
import ContentfulRichTextRenderer from "~/components/ContentfulRichTextRenderer"
import ArrowRight from "~/icons/ArrowRight"
import ResponsiveContentfulImage from "~/components/ResponsiveContentfulImage"
import { DateTime } from "luxon"

declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ImgHTMLAttributes<T> {
    fetchpriority?: "high" | "low" | "auto"
  }
}

type ImageItem = {
  type: "IMAGE"
  url?: string
  altText?: string | null
}

type ContentfulImageItem = {
  type: "CONTENTFUL_IMAGE"
  url: string
  srcMobile?: string
  altText?: string | null
  width?: number | null
  height?: number | null
  widthMobile?: number | null
  heightMobile?: number | null
  sizeMobile: string
  sizeDesktop: string
  sizes?: string
}

// We might want to add video items later.
type MediaItem = ImageItem | ContentfulImageItem

type Alignment = "left" | "justify" | "center" | "right"

type MediaPosition = "top" | "bottom"

type HeaderLevel = "h2" | "h3" | "h4"

interface ButtonProps {
  text: string
  url?: string
  openInNewWindow?: boolean
  color?: string
  textColor?: string
  style?: "normal" | "inverted" | "outlined"
}

interface ImageAboveHeadingProps {
  src: string
  srcMobile?: string
  alt?: string | null
  width?: number
  height?: number
  widthMobile?: number
  heightMobile?: number
  sizeMobile?: string
  sizeDesktop?: string
  sizes?: string
  className?: string
}

export interface Props {
  media: MediaItem | null
  mediaPosition?: MediaPosition
  imageMaxWidth?: number
  imageFillMode?: "fill" | "fit"
  imagePadding?: number
  imageAlignment?: "Left" | "Center" | "Right"
  badge?: {
    text: string
    color?: string
  }
  tag?: string
  header?: string
  headerLevel?: HeaderLevel
  body?: { json: any }
  textAlign?: Alignment
  textColor?: string
  backgroundColor?: string
  backgroundImage?: string
  button?: ButtonProps
  rounded?: boolean
  countdownTargetDateTime?: string
  imageAboveHeading?: ImageAboveHeadingProps
  isMainContent?: boolean
  shouldEagerLoad?: boolean
  overlayText?: boolean
}

type Components = {
  Media: FC<{
    media: MediaItem
    fullHeight?: boolean
    mediaPosition?: MediaPosition
    imageFillMode?: "fill" | "fit"
    imageMaxWidth?: number
    imagePadding?: number
    lazyLoad?: boolean
    imageAlignment?: "Left" | "Center" | "Right"
  }>
  Info: FC<{
    tag?: string
    header?: string
    headerLevel?: HeaderLevel
    body?: { json: any }
    textColor?: string
    button?: ButtonProps
    mediaPosition?: MediaPosition
    countdownTargetDateTime?: string
    imageAboveHeading?: ImageAboveHeadingProps
    textAlign?: Alignment
    lazyLoad?: boolean
    overlayText?: boolean
  }>
  CountdownClock: FC<{
    targetDateTime: string
  }>
}

const VerticalCard: FC<Props> & Components = ({
  backgroundColor,
  backgroundImage,
  tag,
  header,
  headerLevel,
  body,
  button,
  textColor,
  media,
  badge,
  textAlign,
  mediaPosition,
  imageFillMode,
  imageMaxWidth,
  imagePadding,
  imageAlignment,
  rounded,
  countdownTargetDateTime,
  imageAboveHeading,
  isMainContent,
  shouldEagerLoad,
  overlayText
}) => {
  const hasInfo = tag || header || body || button || countdownTargetDateTime
  const lazyLoad = !isMainContent && !shouldEagerLoad

  return (
    <div
      className={classNames(
        "relative flex h-full flex-col justify-between overflow-hidden bg-cover bg-center",
        rounded ? "rounded-xl" : "",
        {
          "text-left": !textAlign || textAlign == "left",
          "text-justify": textAlign == "justify",
          "text-center": textAlign == "center",
          "text-right": textAlign == "right"
        }
      )}
      style={{
        backgroundColor: backgroundColor ?? "white",
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined
      }}
    >
      {badge && (
        <div
          className="absolute right-6 top-4 z-10 flex h-6 items-center rounded-full bg-blue-light-300 p-2.5 text-center text-sm text-white"
          style={{ backgroundColor: badge.color }}
        >
          {badge.text}
        </div>
      )}

      {media && (
        <VerticalCard.Media
          media={media}
          fullHeight={!hasInfo}
          mediaPosition={mediaPosition}
          imageFillMode={imageFillMode}
          imageMaxWidth={imageMaxWidth}
          imagePadding={imagePadding}
          imageAlignment={imageAlignment}
          lazyLoad={lazyLoad}
        />
      )}

      {hasInfo && (
        <VerticalCard.Info
          tag={tag}
          header={header}
          headerLevel={headerLevel}
          body={body}
          button={button}
          textColor={textColor}
          mediaPosition={mediaPosition}
          countdownTargetDateTime={countdownTargetDateTime}
          imageAboveHeading={imageAboveHeading}
          textAlign={textAlign}
          lazyLoad={lazyLoad}
          overlayText={overlayText}
        />
      )}
    </div>
  )
}

const VerticalCardMedia: Components["Media"] = ({
  media,
  fullHeight,
  mediaPosition,
  imageFillMode,
  imageMaxWidth,
  imagePadding,
  imageAlignment,
  lazyLoad
}) => {
  if (!media.url) return null

  if (media.type == "CONTENTFUL_IMAGE") {
    return (
      <div
        className={classNames({
          "flex-grow": fullHeight,
          "order-2": mediaPosition == "bottom"
        })}
      >
        <ResponsiveContentfulImage
          src={media.url}
          srcMobile={media.srcMobile}
          sizeMobile={media.sizeMobile}
          sizeDesktop={media.sizeDesktop}
          sizes={media.sizes}
          width={media.width ?? undefined}
          height={media.height ?? undefined}
          widthMobile={media.widthMobile ?? undefined}
          heightMobile={media.heightMobile ?? undefined}
          alt={media.altText ?? ""}
          fit={imageFillMode == "fill" ? "fill" : "pad"}
          focus={
            mediaPosition == "bottom" && imageFillMode != "fill"
              ? "bottom"
              : "top"
          }
          style={{
            maxWidth: `min(${imageMaxWidth}px, 100%)`,
            paddingLeft: imagePadding,
            paddingRight: imagePadding,
            paddingTop: imagePadding
          }}
          className={classNames("w-full h-full", {
            "mx-auto":
              imageAlignment == "Center" || imageAlignment == undefined,
            "mr-auto": imageAlignment == "Left",
            "ml-auto": imageAlignment == "Right"
          })}
          lazyLoad={lazyLoad}
        />
      </div>
    )
  }

  return (
    <img
      loading={lazyLoad ? "lazy" : "eager"}
      fetchpriority={lazyLoad ? undefined : "high"}
      src={media.url}
      alt={media.altText ?? ""}
      className={classNames(
        "h-full w-full m-auto",
        imageFillMode == "fill"
          ? "object-cover object-center"
          : "object-contain",
        mediaPosition == "bottom" && imageFillMode != "fill"
          ? "object-bottom"
          : "object-top"
      )}
      style={{
        maxWidth: `min(${imageMaxWidth}px, 100%)`,
        padding: imagePadding
      }}
    />
  )
}
VerticalCard.Media = VerticalCardMedia

const VerticalCardInfo: Components["Info"] = ({
  tag,
  header,
  headerLevel,
  body,
  button,
  textColor,
  mediaPosition,
  countdownTargetDateTime,
  imageAboveHeading,
  textAlign,
  lazyLoad,
  overlayText
}) => {
  const HeaderTag: HeaderLevel =
    headerLevel === "h2" || headerLevel === "h4" ? headerLevel : "h3"
  const shouldOverlayText = overlayText && mediaPosition != "bottom"

  //check if body content exists and is not an empty string
  const hasBody = !!body?.json?.content
    ?.at(0)
    ?.content?.some((content: any) => {
      return content.value != ""
    })

  return (
    <div
      className={classNames("flex flex-col space-y-3.5 px-4 md:px-6", {
        "h-full justify-between py-6 order-1":
          !shouldOverlayText && mediaPosition == "bottom",
        "h-full justify-between py-6":
          !shouldOverlayText && mediaPosition != "bottom",
        "absolute inset-x-0 top-0 z-10 bg-gradient-to-b from-black/70 via-black/45 to-transparent pb-12 pt-6":
          shouldOverlayText
      })}
      style={{
        color: shouldOverlayText ? "#ffffff" : textColor ? textColor : "#002800"
      }}
    >
      <div className="space-y-3.5 max-w-full">
        {tag && <p className="opacity-50">{tag}</p>}
        {imageAboveHeading?.src && (
          <ResponsiveContentfulImage
            src={imageAboveHeading.src}
            srcMobile={imageAboveHeading.srcMobile}
            alt={imageAboveHeading.alt ?? ""}
            width={imageAboveHeading.width}
            height={imageAboveHeading.height}
            widthMobile={imageAboveHeading.widthMobile}
            heightMobile={imageAboveHeading.heightMobile}
            sizeMobile={imageAboveHeading.sizeMobile}
            sizeDesktop={imageAboveHeading.sizeDesktop}
            sizes={imageAboveHeading.sizes}
            className={classNames(
              "block h-auto w-auto max-w-[50%]",
              {
                "mx-auto": textAlign == "center",
                "ml-auto": textAlign == "right",
                "mr-auto":
                  textAlign == "left" ||
                  textAlign == "justify" ||
                  textAlign == undefined
              },
              imageAboveHeading.className
            )}
            lazyLoad={lazyLoad}
          />
        )}
        {header && (
          <HeaderTag
            className={classNames(
              "font-serif text-[1.25rem] leading-none md:text-2xl"
            )}
          >
            {header}
          </HeaderTag>
        )}
        {countdownTargetDateTime && (
          <VerticalCardCountdownClock
            targetDateTime={countdownTargetDateTime}
          />
        )}

        {hasBody && (
          <ContentfulRichTextRenderer
            data={body?.json}
            classes={{
              paragraphClass: classNames("text-base leading-[1.625rem]", {
                "text-white": shouldOverlayText,
                "text-gray-500/80": !shouldOverlayText && !textColor
              }),
              heading3Class: classNames("font-semibold text-2xl", {
                "text-white": shouldOverlayText
              }),
              hyperlinkClass: classNames("underline", {
                "text-white": shouldOverlayText
              }),
              listItemClass: classNames({
                "text-white": shouldOverlayText
              }),
              unorderedListClass: "list-disc pl-5 space-y-1"
            }}
          />
        )}
      </div>
      {button && (
        <Button
          url={button.url}
          openInNewWindow={button.openInNewWindow}
          full
          type="button"
          size="medium"
          style={button.style}
          customTextColor={button.textColor}
          customBackgroundColor={button.color}
          icon={<ArrowRight className="h-3 w-3 opacity-90" />}
        >
          {button.text}
        </Button>
      )}
    </div>
  )
}
VerticalCard.Info = VerticalCardInfo

const VerticalCardCountdownClock: Components["CountdownClock"] = ({
  targetDateTime
}) => {
  const [days, setDays] = useState(0)
  const [hours, setHours] = useState(0)
  const [minutes, setMinutes] = useState(0)
  const [seconds, setSeconds] = useState(0)

  useEffect(() => {
    const intervalId = setInterval(() => {
      const now = DateTime.local()
      const target = DateTime.fromISO(targetDateTime)
      const diff = target
        .diff(now, ["days", "hours", "minutes", "seconds"])
        .toObject()

      if (now <= target) {
        setDays(Math.floor(diff.days ?? 0))
        setHours(Math.floor(diff.hours ?? 0))
        setMinutes(Math.floor(diff.minutes ?? 0))
        setSeconds(Math.floor(diff.seconds ?? 0))
      } else {
        clearInterval(intervalId)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [targetDateTime])

  if (!targetDateTime) return null

  return (
    <div
      className={classNames(
        "flex font-serif text-[1.25rem] leading-none md:text-5xl space-x-4 justify-center"
      )}
    >
      {days > 0 ? (
        <div className="text-center">
          <h1>{days}</h1>
          <p className="text-base md:text-lg">{days > 1 ? "days" : "day"}</p>
        </div>
      ) : (
        <div className="h-14" />
      )}
      {hours > 0 ? (
        <div className="text-center">
          <h1>{hours}</h1>
          <p className="text-base md:text-lg">{hours > 1 ? "hours" : "hour"}</p>
        </div>
      ) : (
        <div className="h-14" />
      )}
      {minutes > 0 ? (
        <div className="text-center">
          <h1>{minutes}</h1>
          <p className="text-base md:text-lg">
            {minutes > 1 ? "minutes" : "minute"}
          </p>
        </div>
      ) : (
        <div className="h-14" />
      )}
      {seconds > 0 ? (
        <div className="text-center">
          <h1>{seconds}</h1>
          <p className="text-base md:text-lg">
            {seconds > 1 ? "seconds" : "second"}
          </p>
        </div>
      ) : (
        <div className="h-14" />
      )}
    </div>
  )
}

VerticalCard.CountdownClock = VerticalCardCountdownClock

export default VerticalCard
